// ============================================================================
// GoalForm — create or edit a goal in a Drawer. Fields map straight to GoalIn:
//   • kind            → free text (e.g. "Fat loss", "Strength")
//   • metric_key      → SelectField of the tracked metrics (weight/bf/bmi/…)
//   • baseline_value  → numeric
//   • target_value    → numeric (required for a measurable goal)
//   • target_date     → DateField
//   • direction       → reduce / gain (auto-inferred default from values)
//   • notes           → text
// Create uses useCreateGoal; edit uses useUpdateGoal. Delete is handled by the
// parent panel. Optional-first: empty values are sent as null.
// ============================================================================
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Banner,
  Button,
  DateField,
  SelectField,
  TextField,
} from "../../components";
import { useCreateGoal, useUpdateGoal } from "../../hooks/goals";
import type { Goal, GoalInput } from "../../api";
import { Drawer } from "./Drawer";

interface GoalFormProps {
  clientId: number;
  /** When set, the form edits this goal; otherwise it creates a new one. */
  goal: Goal | null;
  open: boolean;
  onClose: () => void;
}

const METRIC_OPTIONS = ["weight", "body_fat", "bmi", "whr", "whtr"] as const;

export function GoalForm({ clientId, goal, open, onClose }: GoalFormProps) {
  const { t } = useTranslation("clientDetail");
  const create = useCreateGoal();
  const update = useUpdateGoal();
  const pending = create.isPending || update.isPending;
  const isError = create.isError || update.isError;

  const [kind, setKind] = useState("");
  const [metricKey, setMetricKey] = useState("weight");
  const [baseline, setBaseline] = useState("");
  const [target, setTarget] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [direction, setDirection] = useState<"reduce" | "gain" | "">("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    setKind(goal?.kind ?? "");
    setMetricKey(goal?.metric_key ?? "weight");
    setBaseline(goal?.baseline_value != null ? String(goal.baseline_value) : "");
    setTarget(goal?.target_value != null ? String(goal.target_value) : "");
    setTargetDate(goal?.target_date ?? "");
    setDirection((goal?.direction as "reduce" | "gain") ?? "");
    setNotes(goal?.notes ?? "");
  }, [open, goal]);

  const num = (s: string): number | null => {
    const v = s.trim();
    if (v === "") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const submit = () => {
    const baselineNum = num(baseline);
    const targetNum = num(target);
    // Auto-infer direction if the trainer didn't pick one.
    let dir: "reduce" | "gain" | null = direction || null;
    if (!dir && baselineNum != null && targetNum != null) {
      dir = targetNum < baselineNum ? "reduce" : "gain";
    }
    const body: GoalInput = {
      kind: kind.trim() || null,
      metric_key: metricKey || null,
      baseline_value: baselineNum,
      target_value: targetNum,
      target_date: targetDate || null,
      direction: dir,
      notes: notes.trim() || null,
    };

    if (goal) {
      update.mutate({ goalId: goal.id, body }, { onSuccess: onClose });
    } else {
      create.mutate({ clientId, body }, { onSuccess: onClose });
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={goal ? t("goals.editTitle") : t("goals.newTitle")}
      subtitle={t("goals.formSubtitle")}
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t("actions.cancel")}
          </Button>
          <Button onClick={submit} disabled={pending}>
            {pending ? t("actions.saving") : t("actions.save")}
          </Button>
        </div>
      }
    >
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <TextField
          label={t("goals.kind")}
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          placeholder={t("goals.kindPlaceholder")}
          autoComplete="off"
        />

        <SelectField
          label={t("goals.metric")}
          value={metricKey}
          onChange={(e) => setMetricKey(e.target.value)}
          options={METRIC_OPTIONS.map((m) => ({
            value: m,
            label: t(`trends.tab.${m}`),
          }))}
        />

        <div className="grid grid-cols-2 gap-5">
          <TextField
            label={t("goals.baseline")}
            value={baseline}
            onChange={(e) => setBaseline(e.target.value)}
            type="number"
            inputMode="decimal"
            step="0.01"
            hint={t("goals.baselineHint")}
          />
          <TextField
            label={t("goals.target")}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            type="number"
            inputMode="decimal"
            step="0.01"
          />
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <DateField
            label={t("goals.targetDate")}
            value={targetDate}
            onChange={setTargetDate}
          />
          <SelectField
            label={t("goals.direction")}
            value={direction}
            onChange={(e) => setDirection(e.target.value as typeof direction)}
            options={[
              { value: "", label: t("goals.directionAuto") },
              { value: "reduce", label: t("goals.reduce") },
              { value: "gain", label: t("goals.gain") },
            ]}
          />
        </div>

        <div className="w-full">
          <label
            htmlFor="goal-notes"
            className="mb-1.5 block font-display text-sm font-bold text-body"
          >
            {t("goals.notes")}
          </label>
          <textarea
            id="goal-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
          />
        </div>

        {isError && (
          <Banner tone="danger" title={t("goals.saveError")}>
            {t("goals.saveErrorBlurb")}
          </Banner>
        )}
      </form>
    </Drawer>
  );
}
